import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, of } from 'rxjs';
import { catchError, concatMap, map, retryWhen, take, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { UserBillingActions } from '../../billing/actions';
import { HttpErrorResponse } from '@angular/common/http';
import { AccountActions } from '@app/core/actions';
import { Store } from '@ngrx/store';
import * as fromRoot from '@app/reducers';
import * as fromAdmin from '../../reducers';
import { computePriceInclTax } from '@app/shared/helpers/vat';
import { PaymentStripeService } from '@app/core/services/payment-stripe.service';
import { PriceFormaterPipe } from '@app/shared/pipes/price-formater.pipe';
import { DiscountDurationPipe } from '@app/shared/pipes/discount-duration.pipe';
import { PlanDurationInYearOrMonthPipe } from '@app/shared/pipes/plan-duration-year-or-month.pipe';
import { Billing as Billing072021 } from '@smash-sdk/billing/ejs/07-2021/billing';
import { Billing as Billing012024 } from '@smash-sdk/billing/ejs/01-2024/billing';
import GetSubscriptionError from '@smash-sdk/billing/ejs/01-2024/types/GetSubscription/GetSubscriptionError';
import ListInvoicesError from '@smash-sdk/billing/ejs/01-2024/types/ListInvoices/ListInvoicesError';
import UpdateInformationsError from '@smash-sdk/billing/ejs/01-2024/types/UpdateInformations/UpdateInformationsError';
import { cleanEmptyValuesFromBody } from '@app/shared/helpers/helpers';

@Injectable()
export class BillingEffects {

  loadSubscriptionAndPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.loadSubscriptionAndPlan),
      concatMap(() => {
        const billingSdk = new Billing072021();
        return from(billingSdk.getSubscription()).pipe(
          mergeMap(({ subscription }) => {
            return [UserBillingActions.loadSubscriptionAndPlanSuccess({ subscription, plan: subscription?.plan })];
          }),
          retryWhen(error =>
            error.pipe(
              take(3),
              map((error: HttpErrorResponse) => {
                if (!(error instanceof GetSubscriptionError.InternalServerError)) {
                  throw error;
                }
              })
            )
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserBillingActions.loadSubscriptionAndPlanFailure({ error: error.message }))
          )
        )
      }
      )
    ));

  loadInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.loadInvoices),
      withLatestFrom(
        this.store$.select(fromRoot.getSelectedLanguage),
      ),
      concatMap(([action, language]) => {
        const billingSdk = new Billing012024();
        return from(billingSdk.listInvoices()).pipe(
          map((res) => {
            const priceFormat = new PriceFormaterPipe();
            const invoices = res.invoices.map(invoice => {
              const priceWithCurr = priceFormat.transform(invoice.priceInclTax, { language, currency: invoice?.plan?.currency });

              const planDuration = this.planDurationPipe.transform({ duration: invoice.plan?.duration });
              let description = `${invoice.plan?.name} - ${planDuration}`;

              if (invoice.subscription?.appliedCoupon?.discountDuration) {
                const formattedDuration = this.discountDurationPipe.transform(invoice.subscription.appliedCoupon.discountDuration);
                description += ` + ${formattedDuration}`;
              }

              return { ...invoice.plan, ...invoice.charge, ...invoice, priceWithCurr, description };
            }).sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
            return UserBillingActions.loadInvoicesSuccess({ invoices });
          }
          ),
          retryWhen(error =>
            error.pipe(
              take(3),
              map((error: HttpErrorResponse) => {
                if (!(error instanceof ListInvoicesError.InternalServerError)) {
                  throw error;
                }
              })
            )
          ),
          catchError(error =>
            of(UserBillingActions.loadInvoicesFailure({ error: error.message }))
          )
        )
      }
      )
    ));

  updateBillingInformations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.UpdateBillingInformations),
      concatMap((action) => {
        const billingSdk = new Billing012024();
        // TO Fix cleanEmptyValuesFromBody
        return from(billingSdk.updateInformations(cleanEmptyValuesFromBody(action.data))).pipe(
          mergeMap((res: any) => {
            return [
              UserBillingActions.UpdateBillingInformationsSuccess({ billingInformations: res.informations })
            ];
          }),
          retryWhen(error =>
            error.pipe(
              take(3),
              map((error: HttpErrorResponse) => {
                if (!(error instanceof UpdateInformationsError.InternalServerError)) {
                  throw error;
                }
              })
            )
          ),
          catchError(error =>
            of(UserBillingActions.UpdateBillingInformationsFailure({ error: error.message }))
          )
        )
      }
      )
    ));

  unsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.unsubscribe),
      concatMap((action) => {
        const billingSdk = new Billing012024();
        return from(billingSdk.unsubscribeSubscription({ ...action.reason })).pipe(
          concatMap((res: any) => {
            return [
              UserBillingActions.unsubscribeSuccess({ subscription: res.subscription }),
              AccountActions.loadSubscriptionWithPlan(),
            ];
          }),
          catchError(error =>
            of(UserBillingActions.unsubscribeFailure({ error: error.message }))
          )
        )
      }
      )
    ));
  //
  computePlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.computePlan),
      mergeMap(action =>
        of(action).pipe(
          withLatestFrom(
            this.store$.select(fromRoot.getBillingInformations),
            this.store$.select(fromAdmin.getPlan),
          )
        )
      ),
      switchMap(([action, billingInformations, plan]) => {
        if (plan && billingInformations) {
          const { priceExclTax } = plan;
          const { countryCode, vatNumber } = billingInformations;
          const computedPlan = computePriceInclTax({ priceExclTax, countryCode }, action.country, vatNumber);
          return of(UserBillingActions.computePlanSuccess({ computedPlan }));
        } else {
          return of(UserBillingActions.computePlanFailure({ error: 'Can\'t compute plan priceExclTax' }));
        }
      })
    )
  );

  retrieveStripePaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.retrieveStripePaymentMethod),
      concatMap((action) => {
        const billingSdk = new Billing012024();
        return from(billingSdk.getStripePaymentMethod()).pipe(
          concatMap(({ paymentMethod }) => {
            return of(UserBillingActions.retrieveStripePaymentMethodSuccess({ paymentMethod }));
          }),
          catchError(error =>
            of(UserBillingActions.retrieveStripePaymentMethodError({ error: error.message }))
          )
        );
      })
    )
  );

  updateStripePaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.updateStripePaymentMethod),
      switchMap((action) => {
        const billingSdk = new Billing012024();
        return from(billingSdk.createStripeSetupIntent()).pipe(
          concatMap(({ setupIntent }) => {
            const { clientSecret } = setupIntent;
            return this.paymentStripeService.stripeConfirmSetupIntent({ secret: clientSecret, token: action.cardToken }).then((stripeResponse) => {
              if (stripeResponse?.setupIntent?.status === 'succeeded') {
                return UserBillingActions.updateStripePaymentMethodSuccess();
              } else {
                return UserBillingActions.updateStripePaymentMethodError({ error: 'PREMIUM_BILLING_PAYMENT_METHOD_ERROR_MESSAGE' });
              }
            });
          }),
          catchError(error =>
            of(UserBillingActions.updateStripePaymentMethodError({ error: 'PREMIUM_BILLING_PAYMENT_METHOD_ERROR_MESSAGE' }))
          )
        );
      })
    )
  );

  updateStripePaymentMethodSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserBillingActions.updateStripePaymentMethodSuccess),
      map(() => UserBillingActions.retrieveStripePaymentMethod())
    )
  );

  constructor(
    private actions$: Actions,
    private readonly paymentStripeService: PaymentStripeService,
    private store$: Store<fromRoot.State & fromAdmin.State>,
    private discountDurationPipe: DiscountDurationPipe,
    private planDurationPipe: PlanDurationInYearOrMonthPipe
  ) { }

}
